import React, { useEffect, useState } from "react";
import { FiLock } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import RenderToast from "../components/RenderToast";
import ChangePassword from "../modal/ChangePassword";
import { selectAdminProfile, updateAdminProfile } from "../store/authSlice";
import { openModal, selectModal } from "../store/uiSlice";
import { isEmailValid } from "../utils/security";
import { changeAdminPassword, saveAdminProfile } from "../services/portalApi";
import styles from "./AdminProfilePage.module.css";

export default function AdminProfilePage() {
  const profile = useSelector(selectAdminProfile);
  const modal = useSelector(selectModal);
  const dispatch = useDispatch();
  const [form, setForm] = useState(profile);
  const [saving, setSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);

  const update = (name) => (event) =>
    setForm((prev) => ({ ...prev, [name]: event.target.value }));

  useEffect(() => {
    setForm(profile);
  }, [profile]);

  const handleSave = () => {
    if (!form.email) {
      RenderToast({ type: "error", message: "Email is required." });
      return;
    }
    if (!isEmailValid(form.email)) {
      RenderToast({ type: "error", message: "Please enter a valid email." });
      return;
    }
    setSaving(true);
    saveAdminProfile({
      name: form.name || "",
      email: form.email,
    })
      .then(() => {
        dispatch(
          updateAdminProfile({
            ...form,
            mustUpdateCredentials: false,
          })
        );
        RenderToast({ type: "success", message: "Profile saved successfully" });
      })
      .catch((error) => RenderToast({ type: "error", message: error.message || "Unable to save profile" }))
      .finally(() => setSaving(false));
  };

  const handlePasswordSubmit = ({ currentPassword, newPassword, confirmPassword }) => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      RenderToast({ type: "error", message: "All password fields are required." });
      return;
    }
    if (newPassword !== confirmPassword) {
      RenderToast({ type: "error", message: "New password and confirm password must match." });
      return;
    }
    setPasswordSaving(true);
    changeAdminPassword({ currentPassword, newPassword, confirmPassword })
      .then(() => {
        dispatch(
          updateAdminProfile({
            password: newPassword,
            mustUpdateCredentials: false,
          })
        );
        RenderToast({ type: "success", message: "Password updated successfully" });
        dispatch(openModal({ name: null }));
      })
      .catch((error) => RenderToast({ type: "error", message: error.message || "Unable to change password" }))
      .finally(() => setPasswordSaving(false));
  };

  return (
    <div className="pageCard" style={{ padding: "1.3rem" }}>
      <div className={styles.head}>
        <div>
          <h2 className="sectionTitle">Profile Settings</h2>
          <p className="muted">Update admin contact details and password.</p>
        </div>
        <button type="button" className="btnBase btnSecondary" onClick={() => dispatch(openModal({ name: "changePassword" }))}>
          <FiLock />
          Change Password
        </button>
      </div>

      {profile.mustUpdateCredentials ? (
        <div className={styles.notice}>
          First login detected. Please update email and password.
        </div>
      ) : null}

      <div className={styles.grid}>
        <label className={styles.field}>
          <span>Name</span>
          <input className="inputBase" value={form.name || ""} onChange={update("name")} />
        </label>
        <label className={styles.field}>
          <span>Email *</span>
          <input className="inputBase" value={form.email || ""} onChange={update("email")} />
        </label>
      </div>

      <div className={styles.actions}>
        <button type="button" className="btnBase btnPrimary" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </button>
      </div>

      <ChangePassword
        open={modal === "changePassword"}
        onClose={() => dispatch(openModal({ name: null }))}
        loading={passwordSaving}
        onSubmit={handlePasswordSubmit}
      />
    </div>
  );
}
